import { CircularProgress } from '@mui/material';
import { Base64 } from 'js-base64';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import Button from 'js/components/elements/editors/mui/Button';
import Constants from 'js/Constants';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import FormatUtils from 'js/lib/FormatUtils';
import {
  CodeSection,
  DEFAULT_CODE,
  FilePreview,
  useTestCustomProcessor,
} from 'new/components/flows/shared/customProcessor';
import testClasses from 'new/components/flows/shared/customProcessor/TestCustomProcessor.module.scss';
import ReusableCoreHeader from 'new/components/ReusableTransform/ReusableCore/ReusableCoreHeader';
import useForms from 'new/state/forms/repository/useForms';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classes from './EditCustomProcessor.module.scss';

const DEFAULT_CONFIG = {
  data_credentials_id: 12345,
  source_config: {
    advanced_settings: 'Auto Detect',
    'custom_runtime.mode': 'PARSING',
    path_exclusions: false,
    'schema.detection.once': false,
    allowGrouping: false,
  },
  code_container_config: {
    packages: '',
    extra_data: '',
  },
};

export default function EditCustomProcessor({ transformBeingEdited }) {
  const transformCode = useSelector(
    (state: any) => state.transforms.allTransforms.decoded[transformBeingEdited?.id]?.decoded,
  );
  const { data: formData, updateFormData } = useForms(FORM_TYPE_CREATE_REUSABLE_TX);
  const path = formData?.path || '';
  const code = formData?.customProcessorCode || '';
  const config = formData?.customProcessorConfig || '';
  let parsedConfig: any | null = null;

  try {
    parsedConfig = JSON.parse(config);
  } catch (e) {
    parsedConfig = null;
  }

  const credentialId = parsedConfig?.data_credentials_id;
  const { isLoadingParserOutput, isLoadingTestResult, parserOutput, testResult, test } =
    useTestCustomProcessor(credentialId);
  const isLoading = isLoadingParserOutput || isLoadingTestResult;

  useEffect(() => {
    // initialize form data
    let customProcessorConfig = { ...DEFAULT_CONFIG };

    if (transformBeingEdited) {
      customProcessorConfig.code_container_config =
        transformBeingEdited.code_config || DEFAULT_CONFIG.code_container_config;
    }

    updateFormData({
      customProcessorCode: transformCode || DEFAULT_CODE,
      customProcessorConfig: FormatUtils.formatCode(
        JSON.stringify(customProcessorConfig),
        'application/json',
      ),
      path: '',
    });
  }, []);

  const onCodeChanged = (value: string) => {
    updateForm('customProcessorCode', value);
  };

  const onConfigChanged = (value: string) => {
    updateForm('customProcessorConfig', value);
  };

  const onPathChanged = (value: string) => {
    updateForm('path', value);
  };

  const onTest = () => {
    const codeBase64 = Base64.encode(code);
    const source_config = parsedConfig?.source_config || DEFAULT_CONFIG.source_config;
    const code_container_config =
      parsedConfig?.code_container_config || DEFAULT_CONFIG.code_container_config;

    test(codeBase64, path, {
      source_config,
      code_container_config,
    });
  };

  const updateForm = (key, value) => {
    updateFormData({
      [key]: value,
    });
  };

  return (
    <div className={classes.root}>
      <ReusableCoreHeader transformID={transformBeingEdited?.id} />
      <div className={classes.body}>
        <div className={classes.section}>
          <h3 className={classes.sectionHeading}>Processor Function</h3>
          <LoadableMonacoEditor
            language={Constants.CodeLanguages.PYTHON}
            theme="dark"
            value={code}
            onChange={onCodeChanged}
          />
        </div>
        <div className={classes.section}>
          <h3 className={classes.sectionHeading}>Test Data</h3>
          <FilePreview credentialId={credentialId} path={path} onChange={onPathChanged} />
          <LoadableMonacoEditor
            language="json"
            theme="dark"
            height={300}
            value={config}
            onChange={onConfigChanged}
          />
        </div>
        <div className={classes.section}>
          <h3 className={classes.sectionHeading}>Processor Output</h3>
          <div className={`${testClasses.testButtonContainer} ${classes.testButtonContainer}`}>
            <Button
              className={testClasses.testButton}
              variant="ghost"
              color="secondary"
              disabled={isLoading || !credentialId}
              onClick={onTest}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Test'}
            </Button>
            <div className={testClasses.infoContainer}>
              Processing may take up to 1 minute or more.
            </div>
          </div>
          <div className={classes.outputCodeSections}>
            <CodeSection
              className={classes.codeSection}
              heading="Nexla Parser Output"
              subheading={parsedConfig?.source_config?.advanced_settings || '(No parser specified)'}
              isLoading={isLoadingParserOutput}
              result={parserOutput}
            />
            <CodeSection
              className={classes.codeSection}
              heading="Custom Post Processor Output"
              subheading={path || '(No file selected)'}
              isLoading={isLoadingTestResult}
              result={testResult}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
